import { Component } from '@theme/component';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  getCart,
  initFreeGiftConfig,
  needsFreeGiftChange,
  setFreeGiftRefreshCallback,
  setFreeGiftSectionId,
  syncFreeGift,
} from '@theme/custom-cart-free-gift';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} overlay
 * @property {HTMLElement} drawer
 * @property {HTMLElement} [cartItemCount]
 *
 * @extends {Component<Refs>}
 */
class CustomCartDrawerComponent extends Component {
  requiredRefs = ['overlay', 'drawer'];

  static #GUARANTEE_TIMER_START_KEY = 'custom-cart-guarantee-timer-start';
  static #GUARANTEE_TIMER_DURATION_KEY = 'custom-cart-guarantee-timer-duration';

  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** @type {number | null} */
  #guaranteeTimerInterval = null;

  /** @type {number | null} */
  #freeGiftSyncTimer = null;

  connectedCallback() {
    super.connectedCallback();

    try {
      initFreeGiftConfig(this.#readFreeGiftDataset());
      setFreeGiftSectionId(this.sectionId);
      setFreeGiftRefreshCallback((cart, sectionHtml) => this.#refreshAfterFreeGift(cart, sectionHtml));
    } catch (error) {
      console.warn('[FreeGift] Init failed, cart drawer continues:', error);
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener('keydown', this.#handleKeydown);
    this.#startGuaranteeTimer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener('keydown', this.#handleKeydown);
    this.#stopGuaranteeTimer();
    this.#clearFreeGiftSyncTimer();
    this.#unlockScroll();
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Custom cart drawer section id missing');
    return sectionId;
  }

  get isOpen() {
    return this.refs.drawer?.classList.contains('is-active') ?? false;
  }

  open() {
    if (this.isOpen) return;

    this.#previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    this.refs.overlay.classList.add('is-active');
    this.refs.drawer.classList.add('is-active');
    this.refs.overlay.setAttribute('aria-hidden', 'false');
    this.refs.drawer.setAttribute('aria-hidden', 'false');
    this.#lockScroll();

    const closeButton = this.refs.drawer.querySelector('.custom-cart-drawer__close');
    if (closeButton instanceof HTMLElement) {
      closeButton.focus();
    }

    this.#startGuaranteeTimer();
    this.#scheduleFreeGiftSync('drawer_open');
  }

  close() {
    if (!this.isOpen) return;

    this.refs.overlay.classList.remove('is-active');
    this.refs.drawer.classList.remove('is-active');
    this.refs.overlay.setAttribute('aria-hidden', 'true');
    this.refs.drawer.setAttribute('aria-hidden', 'true');
    this.#unlockScroll();

    if (this.#previouslyFocused) {
      this.#previouslyFocused.focus();
      this.#previouslyFocused = null;
    }
  }

  /**
   * @param {Event} event
   */
  decreaseQuantity(event) {
    event.preventDefault();
    const line = this.#getLineFromEvent(event);
    if (!line || this.#isFreeGiftLine(line)) return;

    const currentQty = this.#getLineQuantity(line);
    this.#changeQuantity(line, Math.max(0, currentQty - 1));
  }

  /**
   * @param {Event} event
   */
  increaseQuantity(event) {
    event.preventDefault();
    const line = this.#getLineFromEvent(event);
    if (!line || this.#isFreeGiftLine(line)) return;

    const currentQty = this.#getLineQuantity(line);
    this.#changeQuantity(line, currentQty + 1);
  }

  /**
   * @param {Event} event
   */
  removeItem(event) {
    event.preventDefault();
    const line = this.#getLineFromEvent(event);
    if (!line || this.#isFreeGiftLine(line)) return;

    this.#changeQuantity(line, 0);
  }

  /**
   * @param {number} line
   * @param {number} quantity
   */
  async #changeQuantity(line, quantity) {
    this.refs.drawer.classList.add('is-updating');

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.sectionId,
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const parsedResponse = await response.json();

      if (parsedResponse.errors) {
        console.error(parsedResponse.errors);
        return;
      }

      const sectionHtml = parsedResponse.sections?.[this.sectionId];
      if (sectionHtml) {
        await this.#morphSection(sectionHtml);
      } else {
        await this.#renderSection();
      }

      const newItemCount = this.#parseItemCount(parsedResponse, sectionHtml);

      document.dispatchEvent(
        new CartUpdateEvent(parsedResponse, this.sectionId, {
          itemCount: newItemCount,
          source: 'custom-cart-drawer-component',
          sections: parsedResponse.sections,
        })
      );

      this.#scheduleFreeGiftSync('quantity_change', parsedResponse);
    } catch (error) {
      console.error(error);
    } finally {
      this.refs.drawer?.classList.remove('is-updating');

      if (this.isOpen) {
        this.#lockScroll();
      }
    }
  }

  /**
   * @param {CartAddEvent | CartUpdateEvent} event
   */
  #handleCartUpdate = async (event) => {
    const source = event.detail?.data?.source;

    if (source === 'custom-cart-drawer-component') {
      if (this.hasAttribute('auto-open')) {
        this.open();
      }
      return;
    }

    if (source === 'free-gift-sync') {
      if (this.hasAttribute('auto-open')) {
        this.open();
      }
      return;
    }

    const sections = event.detail?.data?.sections;

    try {
      if (sections?.[this.sectionId]) {
        await this.#morphSection(sections[this.sectionId]);
      } else {
        await this.#renderSection();
      }
    } catch (error) {
      console.error(error);
    }

    if (this.hasAttribute('auto-open')) {
      this.open();
    }

    this.#scheduleFreeGiftSync('cart_update');
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    event.preventDefault();
    this.close();
  };

  /**
   * @param {string} sectionHtml
   */
  async #morphSection(sectionHtml) {
    const wasOpen = this.isOpen;
    await morphSection(this.sectionId, sectionHtml);
    if (wasOpen) {
      requestAnimationFrame(() => this.#restoreOpenState());
    }
  }

  async #renderSection() {
    const wasOpen = this.isOpen;
    await sectionRenderer.renderSection(this.sectionId, { cache: false });
    if (wasOpen) {
      requestAnimationFrame(() => this.#restoreOpenState());
    }
  }

  #restoreOpenState() {
    if (!this.refs.overlay || !this.refs.drawer) return;

    this.refs.overlay.classList.add('is-active');
    this.refs.drawer.classList.add('is-active');
    this.refs.overlay.setAttribute('aria-hidden', 'false');
    this.refs.drawer.setAttribute('aria-hidden', 'false');
    this.#lockScroll();
    this.#startGuaranteeTimer();
  }

  /**
   * @param {object | null} cart
   * @param {string | null} [sectionHtml]
   */
  async #refreshAfterFreeGift(cart, sectionHtml = null) {
    const wasOpen = this.isOpen;
    this.refs.drawer?.classList.add('is-updating');

    try {
      if (sectionHtml) {
        await morphSection(this.sectionId, sectionHtml);
      } else {
        await this.#renderSection();
      }

      if (wasOpen) {
        requestAnimationFrame(() => this.#restoreOpenState());
      }
    } finally {
      this.refs.drawer?.classList.remove('is-updating');
    }

    if (cart) {
      document.dispatchEvent(
        new CartUpdateEvent(cart, this.sectionId, {
          itemCount: cart.item_count,
          source: 'free-gift-sync',
        })
      );
    }
  }

  /**
   * @param {string} source
   * @param {object} [cartOverride]
   */
  #scheduleFreeGiftSync(source, cartOverride) {
    if (this.#freeGiftSyncTimer) {
      clearTimeout(this.#freeGiftSyncTimer);
    }

    this.#freeGiftSyncTimer = window.setTimeout(() => {
      this.#freeGiftSyncTimer = null;
      this.#runFreeGiftSyncSafely(source, cartOverride);
    }, 300);
  }

  #clearFreeGiftSyncTimer() {
    if (this.#freeGiftSyncTimer) {
      clearTimeout(this.#freeGiftSyncTimer);
      this.#freeGiftSyncTimer = null;
    }
  }

  /**
   * @param {string} source
   * @param {object} [cartOverride]
   */
  async #runFreeGiftSyncSafely(source, cartOverride) {
    try {
      const cart = cartOverride?.items ? cartOverride : await getCart();

      if (!needsFreeGiftChange(cart)) {
        return;
      }

      await syncFreeGift(cart, { source });
    } catch (error) {
      console.warn('[FreeGift] Cart drawer gift sync skipped safely:', error);
    }
  }

  /**
   * @param {number} line
   * @returns {boolean}
   */
  #isFreeGiftLine(line) {
    const row = this.refs.drawer.querySelector(`[data-cart-line="${line}"]`);
    return row instanceof HTMLElement && row.dataset.isFreeGift === 'true';
  }

  /**
   * @returns {object}
   */
  #readFreeGiftDataset() {
    return {
      enabled: this.dataset.freeGiftEnabled,
      giftVariantId: this.dataset.freeGiftVariantId,
      requiredQuantity: this.dataset.freeGiftRequiredQuantity,
      qualifyingProductHandles: this.dataset.freeGiftQualifyingHandles,
    };
  }

  #startGuaranteeTimer() {
    this.#stopGuaranteeTimer();
    this.#updateGuaranteeTimer();

    const timerEl = this.refs.guaranteeTimer;
    if (!(timerEl instanceof HTMLElement)) return;

    this.#guaranteeTimerInterval = window.setInterval(() => this.#updateGuaranteeTimer(), 1000);
  }

  #stopGuaranteeTimer() {
    if (this.#guaranteeTimerInterval !== null) {
      clearInterval(this.#guaranteeTimerInterval);
      this.#guaranteeTimerInterval = null;
    }
  }

  #updateGuaranteeTimer() {
    const timerEl = this.refs.guaranteeTimer;
    if (!(timerEl instanceof HTMLElement)) {
      this.#stopGuaranteeTimer();
      return;
    }

    const duration = Number.parseInt(timerEl.dataset.duration ?? '300', 10);
    const durationSeconds = Number.isNaN(duration) ? 300 : duration;
    const remaining = this.#getGuaranteeTimerRemaining(durationSeconds);

    timerEl.textContent = this.#formatTimerDisplay(remaining);

    if (remaining <= 0) {
      this.#stopGuaranteeTimer();
    }
  }

  /**
   * Persists countdown across page reloads for the browser session.
   * Uses a fixed start timestamp — never resets until the session ends.
   *
   * @param {number} durationSeconds
   * @returns {number}
   */
  #getGuaranteeTimerRemaining(durationSeconds) {
    const startKey = CustomCartDrawerComponent.#GUARANTEE_TIMER_START_KEY;
    const durationKey = CustomCartDrawerComponent.#GUARANTEE_TIMER_DURATION_KEY;
    const storedStart = sessionStorage.getItem(startKey);
    const storedDuration = Number.parseInt(sessionStorage.getItem(durationKey) ?? '', 10);

    let startMs = Number.parseInt(storedStart ?? '', 10);

    if (Number.isNaN(startMs) || storedDuration !== durationSeconds) {
      startMs = Date.now();
      sessionStorage.setItem(startKey, String(startMs));
      sessionStorage.setItem(durationKey, String(durationSeconds));
    }

    const elapsed = Math.floor((Date.now() - startMs) / 1000);
    return Math.max(0, durationSeconds - elapsed);
  }

  /**
   * @param {number} totalSeconds
   * @returns {string}
   */
  #formatTimerDisplay(totalSeconds) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  #lockScroll() {
    document.documentElement.setAttribute('scroll-lock', '');
  }

  #unlockScroll() {
    document.documentElement.removeAttribute('scroll-lock');
  }

  /**
   * @param {Event} event
   * @returns {number | null}
   */
  #getLineFromEvent(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;

    const item = target.closest('[data-cart-line]');
    if (!(item instanceof HTMLElement)) return null;

    const line = Number.parseInt(item.dataset.cartLine ?? '', 10);
    return Number.isNaN(line) ? null : line;
  }

  /**
   * @param {number} line
   * @returns {number}
   */
  #getLineQuantity(line) {
    const row = this.refs.drawer.querySelector(`[data-cart-line="${line}"]`);
    const qtyEl = row?.querySelector('[data-cart-quantity]');
    const qty = Number.parseInt(qtyEl?.textContent ?? '0', 10);
    return Number.isNaN(qty) ? 0 : qty;
  }

  /**
   * @param {object} parsedResponse
   * @param {string} [sectionHtml]
   * @returns {number}
   */
  #parseItemCount(parsedResponse, sectionHtml) {
    if (typeof parsedResponse.item_count === 'number') {
      return parsedResponse.item_count;
    }

    if (sectionHtml) {
      const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const countText = doc.querySelector('[ref="cartItemCount"]')?.textContent;
      const count = Number.parseInt(countText ?? '', 10);
      if (!Number.isNaN(count)) return count;
    }

    const countText = this.refs.cartItemCount?.textContent;
    const count = Number.parseInt(countText ?? '', 10);
    return Number.isNaN(count) ? 0 : count;
  }
}

if (!customElements.get('custom-cart-drawer-component')) {
  customElements.define('custom-cart-drawer-component', CustomCartDrawerComponent);
}
